import { Box, Card, CardContent, CardHeader, Divider, getStepConnectorUtilityClass, ListItem, Stack, Step, StepConnector, StepContent, StepLabel, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import PatientInfoCard from './PatientInfoCard';
import { GetCycleResponseItem } from './types';
import PatientInfoDrawer from './PatientInfoDrawer';
import TimelineChart from '../../components/report/charts/TimelineChart';
import ListCard from '../../components/report/ListCard';
import { Badge, Building, Check, List, Syringe } from 'lucide-react';
import Stepper from '../../components/report/Stepper';
import BarChartWithLabelLines from '../../components/report/charts/BarChartWithLabelLines';
import HalfPieCard from '../../components/report/charts/HalfPieCard';
import HalfPieChart from '../../components/report/charts/HalfPieChart';
import CardWithHeader from '../../components/cards/CardWithHeader';
import { parseISO } from 'date-fns';



const IconWithRoundBg: React.FC<{ bgColor: string, icon: ReactElement }> = ({ icon, bgColor }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: bgColor,
            color: 'white',
            width: 32,
            height: 32,
        }}
    >
        {icon}
    </Box>
);



const Analysis: React.FC<{ withReviews: boolean, title: string }> = ({ withReviews, title }) => {

    const theme = useTheme();
    const [patientInfoExpanded, setPatientInfoExpanded] = useState<boolean>(false);
    const [cycle, setCycle] = useState<GetCycleResponseItem | null>({
        id: "1",
        patient: "John Doe",
        birth: "1990-01-01",
        height: 180,
        weight: 80,
        updated: "2023-01-01",
        created: "2023-01-01",
        afc_left: 10,
        afc_right: 10,
        amh: 10,
        fsh: 10,
        diagnosis: "diagnosis",
    });

    const pie = useCallback((title: string, rate: number, caption: string) => (
        <Box width={"100%"}>
            <Card sx={{ height: "100%", padding: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Typography variant="report.card.title" gutterBottom>
                    {title}
                </Typography>
                <HalfPieChart width={220} height={120} labels={['a', 'b']} rate={rate} />
                <Box textAlign={'center'} width={"100%"}>
                    <Typography variant="report.info.label">
                        {caption}
                    </Typography>
                </Box>
            </Card>
        </Box>
    ), []);

    const ohssRiskLevelTable = useMemo(() => (
        <Stack gap={1}>
            {[{
                title: "Coasting",
                value: "Yes"
            }, {
                title: "Cabergoline",
                value: "Yes, 0.5 mg daily for 8 days starting on Day 12"
            }, {
                title: "Albumin",
                value: "No"
            },
            {
                title: "Freeze-All Recommendation",
                value: "Yess"
            }].map((item, index) => (
                <React.Fragment key={index}>
                    <Stack direction={"row"} gap={2}>
                        <Stack direction={"row"} gap={1} width={220}>
                            <Check color='green' />
                            <Typography variant="report.info.label">{item.title}</Typography>
                        </Stack>
                        <Typography variant="report.info.value">{item.value}</Typography>
                    </Stack>
                    <Divider />
                </React.Fragment>
            ))}
        </Stack>
    ), []);

    const lutealPhaseSupportTable = useMemo(() => (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Medication</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Dosage & Frequency</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Duration</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {[{
                    medication: "Progesterone",
                    type: "Oral",
                    dosage: "200 mg twice daily",
                    startDate: parseISO("2023-01-01"),
                    duration: "14 days"
                },
                {
                    medication: "Progesterone",
                    type: "Vaginal",
                    dosage: "200 mg twice daily",
                    startDate: parseISO("2023-01-01"),
                    duration: "14 days"
                }
                ].map((item, index) => (
                    <TableRow>
                        <TableCell>{item.medication}</TableCell>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>{item.dosage}</TableCell>
                        <TableCell>{item.startDate.toDateString()}</TableCell>
                        <TableCell>{item.duration}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    ), []);

    const adjunctRecommendationsTable = useMemo(() => (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Adjuvant</TableCell>
                    <TableCell>Recommended</TableCell>
                    <TableCell>Dosage & Frequency</TableCell>
                    <TableCell>Duration</TableCell>
                </TableRow>
            </TableHead>

            <TableBody>
                {[{
                    adjuvant: "Growth Hormone",
                    recommended: "Yes",
                    dosage: "200 mg twice daily",
                    duration: "14 days"
                },
                {
                    adjuvant: "DHEA",
                    recommended: "No",
                    dosage: "200 mg twice daily",
                    duration: "2 months before the start of stimulation"
                },
                {
                    adjuvant: "CoQ10",
                    recommended: "No",
                    dosage: "-",
                    duration: "-"
                },
                {
                    adjuvant: "Vitamin D",
                    recommended: "Yes",
                    dosage: "200 mg twice daily",
                    duration: "Continuous from protocol start through pregnancy test"
                }
                ].map((item, index) => (
                    <TableRow>
                        <TableCell>{item.adjuvant}</TableCell>
                        <TableCell>{item.recommended}</TableCell>
                        <TableCell>{item.dosage}</TableCell>
                        <TableCell>{item.duration}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    ), []);


    return (
        <Box>
            <Typography mb={theme.spacing(2)} variant={"h4"}>{title}</Typography>
            <Stack direction={"row"} gap={3}>
                <Box minWidth={"20vw"} width={"30%"}>
                    {cycle && <PatientInfoCard onExpand={() => setPatientInfoExpanded(true)} cycle={cycle} />}
                </Box>
                <Stack gap={2} width={"70%"}>
                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.label">
                                {'Recommended Stimulation Protocol: Antagonist'}
                            </Typography>
                            <TimelineChart data={[
                                { startDate: "2023-01-01", endDate: "2023-01-02", title: "Cycle Start", icon: "calendar", yIndex: 0, backgroundColor: "#f0f0f0" },
                                { startDate: "2023-01-03", endDate: "2023-01-04", title: "Cycle Start", icon: "calendar", yIndex: 0, backgroundColor: "#c3c3c3" },
                                { startDate: "2023-01-03", endDate: "2023-01-06", title: "Cycle Start", icon: "calendar", yIndex: 1, backgroundColor: "#f0f0f0" },
                                { startDate: "2023-01-01", endDate: "2023-01-10", title: "Cycle Start", icon: "calendar", yIndex: 2, backgroundColor: "#c3c3c3" },
                                { startDate: "2023-01-13", endDate: "2023-01-16", title: "Cycle Start", icon: "calendar", yIndex: 2, backgroundColor: "#f0f0f0" },

                            ]} />

                            <Stack height={320} alignItems={"center"} gap={2} justifyContent={"space-around"} direction={"row"}>
                                <Box width={"100%"} height={"100%"}>
                                    <ListCard
                                        icon={<IconWithRoundBg bgColor='#DDD6FE' icon={<Syringe />} />}
                                        title={"Gonadotropin"}
                                        items={[
                                            { title: "Type", value: "rFSH" },
                                            { title: "Start day", value: "10/09/2024" },
                                            { title: "Duration", value: "10 days" },
                                            { title: "Starting dose", value: "150 IU/day" },
                                            { title: "Total dose", value: "300 IU" },
                                        ]}
                                    />
                                </Box>
                                <Box width={"100%"} height={"100%"}>
                                    <ListCard
                                        icon={<IconWithRoundBg bgColor='#FEF08A' icon={<Syringe />} />}
                                        title={"GnRH"}
                                        items={[
                                            { title: "Type", value: "Antagonist" },
                                            { title: "Start day", value: "10/09/2024" },
                                            { title: "Duration", value: "10 days" },
                                        ]}
                                    />
                                </Box>
                                <Box width={"100%"} height={"100%"}>
                                    <ListCard
                                        icon={<IconWithRoundBg bgColor='#FED7AA' icon={<Syringe />} />}
                                        title={"Trigger Shot"}
                                        items={[
                                            { title: "Type", value: "rHCG" },
                                            { title: "Day", value: "19/09/2024" },
                                        ]}
                                    />
                                </Box>
                            </Stack>
                        </Stack>

                    </Card>

                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.value">
                                {'Suggested Monitoring Schedule'}
                            </Typography>
                            <Typography variant="report.info.label" textAlign={'left'}>
                                Baseline appointment date: <b>10/09/2024</b> (Day 3)
                            </Typography>

                            <Stack direction={"row"} gap={2}>

                                <Stepper steps={[
                                    {
                                        title: "10/09/2024",
                                        icon: <Building />,
                                        subtitle: "Day 3",
                                        details: ["Transvaginal Ultrasound", "Estradiol Level"]
                                    },
                                    {
                                        title: "10/09/2024",
                                        icon: <Building />,
                                        subtitle: "Day 4",
                                        details: ["Transvaginal Ultrasound", "Estradiol Level", "Estradiol Level", "Estradiol Level"]
                                    },
                                    {
                                        title: "10/09/2024",
                                        icon: <Building />,
                                        subtitle: "Day 5",
                                        details: ["Transvaginal Ultrasound", "Estradiol Level"]
                                    },

                                ]} />

                            </Stack>

                        </Stack>

                    </Card>

                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.value">
                                {'Predicted Outcomes'}
                            </Typography>
                            <BarChartWithLabelLines />
                            <Stack direction={"row"} gap={theme.spacing(3)}>
                                {pie("Live Birth per Egg Retrieval", 12, "CI: 30%-40%")}
                                {pie("Live Birth per Egg Retrieval", 12, "CI: 30%-40%")}
                                {pie("Live Birth per Embryo Transfer", 12, "CI: 30%-40%")}
                            </Stack>
                        </Stack>

                    </Card>

                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.value">
                                {'Risk Assessment'}
                            </Typography>

                            <CardWithHeader title={"OHSS Risk Level"}>
                                <Stack gap={2}>
                                    <Typography variant="report.title">Recommended OHSS Prevention Strategies</Typography>

                                    {ohssRiskLevelTable}
                                </Stack>
                            </CardWithHeader>

                        </Stack>
                    </Card>

                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.value">
                                {'Other Potential Risks'}
                            </Typography>

                            <ul>
                                <li>
                                    <Box><Typography variant='report.info.value'>{"Multiple pregnancy"}</Typography></Box>
                                    <Typography variant='report.card.info.label'>
                                        {"High risk due to the use of a high-dose stimulation protocol; recommend single embryo transfer to minimize the risk."}
                                    </Typography>
                                </li>
                            </ul>
                        </Stack>
                    </Card>

                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.value">
                                {'Luteal Phase Support'}
                            </Typography>
                            <Typography variant="report.info.label">
                                {"Duration:"} <b>14</b> {"(until pregnancy test)"}
                            </Typography>

                            <Card>
                                {lutealPhaseSupportTable}
                            </Card>


                        </Stack>
                    </Card>

                    <Card sx={{ padding: theme.spacing(2), border: '1px solid #e0e0e0', borderRadius: theme.borderRadius }}>
                        <Stack gap={theme.spacing(3)}>
                            <Typography fontWeight={"bold"} variant="report.info.value">
                                {'Adjuvant Recommendations'}
                            </Typography>

                            <Card>
                                {adjunctRecommendationsTable}
                            </Card>
                        </Stack>
                    </Card>


                </Stack>
            </Stack>
            {cycle && <PatientInfoDrawer cycle={cycle} open={patientInfoExpanded} onClose={() => setPatientInfoExpanded(false)} />}
        </Box>
    );
};

export default Analysis;
